import React, { useRef, useState, useEffect } from 'react';
import { View, TouchableOpacity } from 'react-native';
import { TimelineItemRow, getPresetAvatarAsset } from '@cubemeet/ui';
import { MdEdit, MdDelete, MdAddPhotoAlternate, MdCheck, MdClose } from 'react-icons/md';
import CardRenderer from './CardRenderer';
import WebIcon from './WebIcon';
import styles from './ItineraryEditor.styles';
import { ORANGE, normalizeExternalLink, coalesceText, resolveMapRouteModeMeta, detectYouTubeEmbed } from './itineraryUtils';
import { useGlobalToast } from '../../../../../apps/web/src/contexts/ToastContext';
import { ProfileModal } from '@cubemeet/ui';
import TransportForm from '@cubemeet/ui/components/cards/TransportForm';
import OtherFormPlaceholder from '@cubemeet/ui/components/cards/placeholders/OtherFormPlaceholder';
import { getAvatarSource, isPresetAvatar } from '@cubemeet/core/utils/avatarImageMapper';
import { createEmptyPoiPayload } from '../utils/emptyPayloads/poi';
import { createEmptyMealPayload } from '../utils/emptyPayloads/meal';
import { createEmptyActivityPayload } from '../utils/emptyPayloads/activity';
import { createEmptyLodgingPayload } from '../utils/emptyPayloads/lodging';
import { createEmptyShoppingPayload } from '../utils/emptyPayloads/shopping';

interface TimelineItemProps {
  it: any;
  index: number;
  arr: any[];
  editor: any;
  selectedDay: any;
  expandedCards: Set<string>;
  toggleExpand: (cardId: string) => void;
  isEditing?: boolean;
  setEditItemIndex?: (i: number | null) => void;
  setEditForm?: (f: any) => void;
  setEditModalVisible?: (v: boolean) => void;
  setOpenPoi: (p: any) => void;
  setInlineEditTarget?: (t: any) => void;
  inlineEditTarget?: { dayIndex: number; itemIndex: number; mode?: string } | null;
  setDirty?: (v: boolean) => void;
  dayIndex: number;
  sourceIndex?: number;
  parseTimeToMinutes: (s?: string) => number | undefined;
  getNowMinutes: () => number;
  isDraggable?: boolean;
  supabaseClient?: any;
  currentUserId?: string | null;
  canEdit?: boolean;
  groupIdForLocal?: string | null;
  memberProfiles?: Record<string, { display_name?: string | null; avatar_url?: string | null }>;
  onSelectFromCardPicker?: (dayIndex: number, itemIndex: number) => void;
  renderDragHandle?: () => React.ReactNode;
  isSelected?: boolean;
  onSelect?: () => void;
  onImageClick?: (images: any[], startIndex: number) => void;
  hideTimeColumn?: boolean;
}

const detectExternalPartner = (website?: string | null, siteName?: string | null): string | null => {
  const normalize = (value?: string | null) => (typeof value === 'string' ? value.trim().toLowerCase() : '');
  const normalizedSite = normalize(siteName);
  const normalizedHost = (() => {
    if (!website || typeof website !== 'string') return '';
    try {
      const href = website.startsWith('http') ? website : `https://${website}`;
      return new URL(href).hostname.trim().toLowerCase();
    } catch {
      return '';
    }
  })();
  const haystack = [normalizedSite, normalizedHost].filter(Boolean);
  const includes = (needle: string) => haystack.some((value) => value.includes(needle));
  if (includes('klook')) return 'klook';
  if (includes('kkday')) return 'kkday';
  if (includes('trip.com') || includes('ctrip')) return 'trip';
  if (includes('booking')) return 'booking';
  if (includes('agoda')) return 'agoda';
  return null;
};

const fallbackFaviconUrl = (favicon?: string | null, website?: string | null): string | null => {
  if (favicon && typeof favicon === 'string' && favicon.trim()) return favicon.trim();
  if (!website || typeof website !== 'string') return null;
  try {
    const href = website.startsWith('http') ? website : `https://${website}`;
    const host = new URL(href).hostname;
    if (!host) return null;
    return `https://www.google.com/s2/favicons?domain=${host}&sz=64`;
  } catch {
    return null;
  }
};

const TimelineItem: React.FC<TimelineItemProps> = ({
  it,
  index,
  arr,
  editor,
  selectedDay,
  expandedCards,
  toggleExpand,
  isEditing,
  setEditItemIndex,
  setEditForm,
  setEditModalVisible,
  setOpenPoi,
  setInlineEditTarget,
  inlineEditTarget,
  setDirty,
  dayIndex,
  sourceIndex,
  parseTimeToMinutes,
  getNowMinutes,
  isDraggable,
  supabaseClient,
  currentUserId,
  canEdit,
  groupIdForLocal,
  memberProfiles,
  onSelectFromCardPicker,
  renderDragHandle,
  isSelected,
  onSelect,
  onImageClick,
  hideTimeColumn,
}) => {
  // --- Video / embed helpers ---
  // toasts
  const { showError, showSuccess, showWarning, showInfo } = useGlobalToast ? useGlobalToast() : { showError: undefined, showSuccess: undefined, showWarning: undefined, showInfo: undefined };

  const attemptDelete = async () => {
    try {
      console.debug('[TimelineItem] attemptDelete:init', {
        title: it?.title,
        type: it?.type,
        dayIndex,
        index,
        sourceIndex,
      });

      const extractEntryId = (obj: any): string | undefined => {
        if (!obj || typeof obj !== 'object') return undefined;
        const candidates = [
          obj.meta && obj.meta.itineraryEntryId,
          obj.meta && obj.meta.itinerary_entry_id,
          obj.id,
          obj.itineraryEntryId,
          obj.payload && obj.payload.id,
          obj.payload && obj.payload.itineraryEntryId,
        ];
        for (const c of candidates) {
          if (c !== null && typeof c !== 'undefined' && String(c).trim()) return String(c);
        }
        return undefined;
      };

      const entryId = extractEntryId(it) || extractEntryId(it.payload) || extractEntryId(it.meta);
      console.debug('[TimelineItem] attemptDelete:entryIdCheck', { entryId, title: it?.title, type: it?.type });

      if (supabaseClient && entryId) {
        try {
          const delRes = await supabaseClient.from('group_itinerary_entries').delete().eq('id', entryId);
          const delErr = delRes && (delRes.error ?? (delRes as any).error);
          if (delErr) {
            console.error('[TimelineItem] server delete error', delErr);
            try { showError && showError('伺服器刪除失敗，已從本地移除'); } catch (e) {}
          } else {
            try { showSuccess && showSuccess('已從時間軸刪除'); } catch (e) {}
          }
        } catch (e) {
          console.error('[TimelineItem] server delete unexpected', e);
          try { showError && showError('刪除失敗，已從本地移除'); } catch (er) {}
        }
      }

      const resolveEditorIndex = () => {
        const dayItems: any[] = editor?.itinerary?.days?.[dayIndex]?.items || [];
        const result = {
          index,
          reason: 'fallback-index',
          preview: dayItems[index],
        };

        if (typeof sourceIndex === 'number' && sourceIndex >= 0) {
          result.index = sourceIndex;
          result.reason = 'source-index';
          result.preview = dayItems[sourceIndex];
          return result;
        }

        try {
          if (entryId) {
            const foundIdx = dayItems.findIndex((item: any) => {
              const candidateId = extractEntryId(item) || extractEntryId(item?.payload) || extractEntryId(item?.meta);
              return candidateId && String(candidateId) === String(entryId);
            });
            if (foundIdx >= 0) {
              result.index = foundIdx;
              result.reason = 'match-entryId';
              result.preview = dayItems[foundIdx];
              return result;
            }
          }

          const sameRefIdx = dayItems.findIndex((item: any) => item === it);
          if (sameRefIdx >= 0) {
            result.index = sameRefIdx;
            result.reason = 'match-reference';
            result.preview = dayItems[sameRefIdx];
            return result;
          }

          const softMatchIdx = dayItems.findIndex((item: any) => {
            if (!item) return false;
            if (item.type !== it?.type) return false;
            if (item.time && it?.time && item.time !== it.time) return false;
            if (item.title && it?.title && item.title !== it.title) return false;
            const payloadTitle = item?.payload?.title && it?.payload?.title && item.payload.title === it.payload.title;
            const payloadMode = item?.payload?.mode && it?.payload?.mode && item.payload.mode === it.payload.mode;
            const sameMapLink = item?.payload?.mapLink && it?.payload?.mapLink && item.payload.mapLink === it.payload.mapLink;
            return payloadTitle || payloadMode || sameMapLink;
          });

          if (softMatchIdx >= 0) {
            result.index = softMatchIdx;
            result.reason = 'match-soft';
            result.preview = dayItems[softMatchIdx];
            return result;
          }
        } catch (err) {
          console.warn('[TimelineItem] resolveEditorIndex soft match failed', err);
        }

        return result;
      };

      if (editor && typeof editor.deleteItem === 'function') {
        const { index: targetIndex, reason, preview } = resolveEditorIndex();
        console.debug('[TimelineItem] attemptDelete:resolved', {
          targetIndex,
          reason,
          previewTitle: preview?.title,
          previewType: preview?.type,
          previewTime: preview?.time,
        });
        // Guard: ensure targetIndex refers to the source array index (day.items). If mismatch, attempt fallback by id matching.
        let finalIndex = targetIndex;
        try {
          const dayItems: any[] = editor?.itinerary?.days?.[dayIndex]?.items || [];
          const candidate = dayItems[finalIndex];
          const extractEntryIdLocal = (obj: any): string | undefined => {
            if (!obj || typeof obj !== 'object') return undefined;
            const candidates = [
              obj.meta && obj.meta.itineraryEntryId,
              obj.meta && obj.meta.itinerary_entry_id,
              obj.id,
              obj.payload && obj.payload.id,
            ];
            for (const c of candidates) { if (c !== null && typeof c !== 'undefined' && String(c).trim()) return String(c); }
            return undefined;
          };
          const candId = extractEntryIdLocal(candidate);
          if (entryId && candId && candId !== String(entryId)) {
            const byIdIdx = dayItems.findIndex((x: any) => extractEntryIdLocal(x) === String(entryId));
            if (byIdIdx >= 0) {
              console.debug('[TimelineItem] attemptDelete:correctedIndexById', { from: finalIndex, to: byIdIdx });
              finalIndex = byIdIdx;
            }
          }
        } catch (e) {
          console.warn('[TimelineItem] attemptDelete index guard failed', e);
        }
        editor.deleteItem(dayIndex, finalIndex);
        try { setDirty && setDirty(true); } catch (err) {}
      }
    } catch (error) {
      console.error('[TimelineItem] delete flow failed', error);
    }
  };

  // === External URL quick-add (placeholder card enhancement) ===
  const [externalUrl, setExternalUrl] = useState<string>('');
  const [externalMetaLoading, setExternalMetaLoading] = useState<boolean>(false);
  const [externalMeta, setExternalMeta] = useState<any>(null);
  const [externalEmbedCode, setExternalEmbedCode] = useState<string>('');
  const extractMetaWatchdog = useRef<number | null>(null);
  const loadingRef = useRef<boolean>(false);
  const [affiliatePartners, setAffiliatePartners] = useState<Array<{ key: string; name: string; searchBase: string; affiliateParam?: string }>>([]);

  useEffect(() => {
    // Attempt to fetch partner config from backend; fallback to static defaults
    (async () => {
      try {
        const res = await fetch('/api/affiliate-partners', { method: 'GET' });
        if (res.ok) {
          const json = await res.json();
          if (Array.isArray(json)) {
            setAffiliatePartners(json);
            return;
          }
        }
      } catch (e) {
        // ignore, use fallback
      }
      setAffiliatePartners([
        { key: 'trip', name: 'Trip.com', searchBase: 'https://www.trip.com/search/?keyword=' },
        { key: 'klook', name: 'Klook', searchBase: 'https://www.klook.com/search/result/?query=' },
        { key: 'kkday', name: 'KKday', searchBase: 'https://www.kkday.com/zh-tw/product/search?keyword=' },
      ]);
    })();
  }, []);

  const extractMeta = async (targetUrl: string) => {
    if (!targetUrl || externalMetaLoading) return;
    setExternalMetaLoading(true);
    loadingRef.current = true;
    setExternalMeta(null);

    // Helper: try fetching from noembed (CORS-friendly oEmbed proxy) for better YouTube metadata
    const tryNoembed = async (url: string) => {
      try {
        const res = await fetch(`https://noembed.com/embed?url=${encodeURIComponent(url)}`);
        if (res.ok) {
          const data = await res.json();
          if (data && !data.error) {
            return {
              title: data.title,
              description: data.author_name ? `By ${data.author_name}` : undefined,
              siteName: data.provider_name || 'YouTube',
              image: data.thumbnail_url,
              favicon: 'https://www.google.com/s2/favicons?domain=youtube.com&sz=64',
              url: url,
              resolvedUrl: url,
              // noembed returns html for embedding, we can use it
              embed: data.html ? { kind: 'html', html: data.html } : undefined
            };
          }
        }
      } catch (e) {
        console.warn('[TimelineItem] noembed fetch failed', e);
      }
      return null;
    };

    try {
      const endpoint = 'https://extract-meta.cubemeet.app/api/extract-meta?url=' + encodeURIComponent(targetUrl.trim());
      console.debug('[TimelineItem] extractMeta -> requesting', { endpoint, targetUrl });
      // watchdog: if fetch doesn't finish in reasonable time, force-reset loading state
      try {
        if (extractMetaWatchdog.current) window.clearTimeout(extractMetaWatchdog.current);
      } catch {}
      extractMetaWatchdog.current = window.setTimeout(() => {
        if (loadingRef.current) {
          console.warn('[TimelineItem] extractMeta -> watchdog timeout, resetting loading state');
          loadingRef.current = false;
          try { setExternalMetaLoading(false); } catch (e) {}
        }
      }, 10000);

      const res = await fetch(endpoint, { method: 'GET', headers: { 'Accept': 'application/json' } });
      console.debug('[TimelineItem] extractMeta -> response', { status: res.status, ok: res.ok });
      
      let success = false;
      if (res.ok) {
        const json = await res.json();
        console.debug('[TimelineItem] extractMeta -> json', json && (typeof json === 'object' ? { keys: Object.keys(json).slice(0,10) } : String(json)));
        
        // Check if result is too generic for YouTube (e.g. title is just "YouTube")
        const isGenericYoutube = (json.siteName === 'YouTube' || json.siteName === 'Google') && (json.title === 'YouTube' || json.title === '- YouTube' || !json.title);
        
        if (isGenericYoutube) {
             const yt = detectYouTubeEmbed(targetUrl);
             if (yt) {
                 const enhanced = await tryNoembed(targetUrl);
                 if (enhanced) {
                     setExternalMeta(enhanced);
                     success = true;
                 } else {
                     // Fallback to local if noembed fails but we have video ID
                     setExternalMeta({
                        ...json,
                        title: 'YouTube Video',
                        image: yt.poster || json.image,
                     });
                     success = true;
                 }
             } else {
                 setExternalMeta(json);
                 success = true;
             }
        } else {
            setExternalMeta(json);
            success = true;
        }
      } 
      
      if (!success) {
        const yt = detectYouTubeEmbed(targetUrl);
        if (yt) {
          // Try noembed first as fallback
          const enhanced = await tryNoembed(targetUrl);
          if (enhanced) {
             setExternalMeta(enhanced);
          } else {
             setExternalMeta({
                title: 'YouTube Video',
                description: 'Watch on YouTube',
                siteName: 'YouTube',
                image: yt.poster,
                favicon: 'https://www.google.com/s2/favicons?domain=youtube.com&sz=64',
                url: targetUrl,
                resolvedUrl: targetUrl
              });
          }
        } else {
          let text = undefined;
          try { text = await res.text(); } catch (e) {}
          console.warn('[TimelineItem] extractMeta -> non-ok response', { status: res.status, body: text });
          setExternalMeta({ error: true, status: res.status, body: text });
        }
      }
    } catch (e) {
      console.error('[TimelineItem] extractMeta -> fetch error', e);
      const yt = detectYouTubeEmbed(targetUrl);
      if (yt) {
        // Try noembed first as fallback
        const enhanced = await tryNoembed(targetUrl);
        if (enhanced) {
            setExternalMeta(enhanced);
        } else {
            setExternalMeta({
              title: 'YouTube Video',
              description: 'Watch on YouTube',
              siteName: 'YouTube',
              image: yt.poster,
              favicon: 'https://www.google.com/s2/favicons?domain=youtube.com&sz=64',
              url: targetUrl,
              resolvedUrl: targetUrl
            });
        }
      } else {
        setExternalMeta({ error: true, message: String(e) });
      }
    } finally {
      try {
        if (extractMetaWatchdog.current) { window.clearTimeout(extractMetaWatchdog.current); extractMetaWatchdog.current = null; }
      } catch {}
      loadingRef.current = false;
      setExternalMetaLoading(false);
    }
  };

  const addExternalUrlItem = async () => {
    console.log('🔥🔥🔥 addExternalUrlItem 被调用了！');
    // Support two modes: normal URL paste, and video-embed-only (iframe code) paste.
    const isVideoEmbedOnly = Boolean(it?.meta?.videoEmbedOnly || it?.meta?.video_embed_only);
    console.log('[TimelineItem] addExternalUrlItem - START', {
      isVideoEmbedOnly,
      currentUserId,
      hasCurrentUserId: !!currentUserId,
    });
    if (isVideoEmbedOnly) {
      // Use embed textarea input
      if (!externalEmbedCode || externalEmbedCode.trim() === '') return;
    } else {
      if (!externalUrl) return;
    }

    const usingEmbed = isVideoEmbedOnly;
    const trimmed = usingEmbed ? externalEmbedCode.trim() : externalUrl.trim();
    let host: string | undefined; try { host = new URL(trimmed).hostname; } catch {}
    const meta = externalMeta && !externalMeta.error ? externalMeta : null;
    // For video-embed-only placeholders we intentionally do NOT auto-fill the title
    const title = isVideoEmbedOnly ? '嵌入內容' : (meta?.title || host || '外部分享');
    const description = meta?.description || null;
    const imageUrl = meta?.image || null;
    const resolvedUrl = meta?.resolvedFinalUrl || meta?.resolvedUrl || null;
    
    // For embed-only mode, try to detect provider info from iframe src
    let detectedProvider: string | null = null;
    let detectedSiteName: string | null = null;
    let detectedFavicon: string | null = null;
    if (usingEmbed) {
      try {
        const srcMatch = trimmed.match(/<iframe[^>]+src=["']([^"']+)["']/i) || trimmed.match(/src=["']?([^"' >]+)/i);
        const src = srcMatch && srcMatch[1] ? srcMatch[1] : (/https?:\/\//.test(trimmed) ? trimmed : null);
        if (src) {
          const srcHost = new URL(src).hostname.toLowerCase();
          if (srcHost.includes('youtube.com') || srcHost.includes('youtu.be')) {
            detectedProvider = 'youtube';
            detectedSiteName = 'YouTube';
            detectedFavicon = 'https://www.google.com/s2/favicons?domain=youtube.com&sz=64';
          } else if (srcHost.includes('bilibili.com')) {
            detectedProvider = 'bilibili';
            detectedSiteName = 'Bilibili';
            detectedFavicon = 'https://www.google.com/s2/favicons?domain=bilibili.com&sz=64';
          }
        }
      } catch (e) {
        // ignore
      }
    }
    
    const partnerKey = meta?.partner || detectExternalPartner(resolvedUrl || trimmed, meta?.siteName || detectedSiteName || host || null);
    const faviconUrl = fallbackFaviconUrl(meta?.favicon || detectedFavicon || null, resolvedUrl || trimmed);
    console.log('[TimelineItem] addExternalUrlItem favicon debug:', {
      detectedFavicon,
      metaFavicon: meta?.favicon,
      resolvedUrl,
      trimmed: trimmed.substring(0, 100),
      finalFaviconUrl: faviconUrl,
      usingEmbed,
    });
    const degradedFlag = typeof meta?.degraded === 'boolean' ? meta.degraded : null;
    // Try to keep the placeholder time if replacing an existing slot
    const currentItems: any[] = editor?.itinerary?.days?.[dayIndex]?.items || [];
    const replaceIdx = typeof sourceIndex === 'number' && sourceIndex >= 0 ? sourceIndex : index;
    const prevItem = (replaceIdx >= 0 && replaceIdx < currentItems.length) ? currentItems[replaceIdx] : null;
    const carryTime = prevItem && prevItem.time ? prevItem.time : null;

    const externalPayload: any = {
      type: 'external',
      title,
      description,
      website: usingEmbed ? null : trimmed,
      imageUrl,
      siteName: meta?.siteName || detectedSiteName || host || null,
      favicon: faviconUrl,
      partner: partnerKey,
      resolvedFinalUrl: resolvedUrl || null,
      degraded: degradedFlag,
      source: { kind: usingEmbed ? 'manual-embed' : 'manual-paste', at: new Date().toISOString() },
    };

    // If this is an embed paste, try to extract iframe src and attach as embed metadata
    if (usingEmbed) {
      try {
        // Check for script tag first (e.g. surveymars, etc.)
        const scriptMatch = trimmed.match(/<script[^>]+src=["']([^"']+)["']/i);
        if (scriptMatch && scriptMatch[1]) {
          externalPayload.embed = { kind: 'html', html: trimmed, src: scriptMatch[1] };
        } else {
          let src: string | null = null;
          // Try common iframe src patterns
          const m1 = trimmed.match(/<iframe[^>]+src=["']([^"']+)["']/i);
          const m2 = trimmed.match(/src=["']?([^"' >]+)/i);
          if (m1 && m1[1]) src = m1[1];
          else if (m2 && m2[1]) src = m2[1];
          // fallback: if the user pasted a plain URL
          if (!src && /https?:\/\//.test(trimmed)) src = trimmed;
          if (src) {
            // Try to detect YouTube and generate full embed metadata
            const yt = detectYouTubeEmbed(src);
            if (yt) {
              externalPayload.embed = yt;
            } else {
              externalPayload.embed = { kind: 'iframe', src: src };
            }
          }
        }
      } catch (e) {
        // ignore parse errors
      }
    } else {
      // If URL is a YouTube link, attach embed metadata so the UI can render a player
      try {
        const yt = detectYouTubeEmbed(resolvedUrl || trimmed);
        if (yt) {
          externalPayload.embed = yt;
        }
      } catch (e) {
        // ignore
      }
    }
    const item: any = {
      id: crypto && crypto.randomUUID ? crypto.randomUUID() : `local-external-${Date.now()}`,
      time: carryTime,
      type: 'external',
      title,
      // Keep both payload and custom_payload so different serializers can pick it up
      payload: externalPayload,
      custom_payload: externalPayload,
      // Ensure this newly added external/embed item is treated as a real card
      // (not a placeholder) so the timeline will render the external preview.
      meta: { placeholderReplaced: true, itemType: 'custom', isPlaceholder: false },
    };
    try {
      // Immutable update: clone itinerary and set via setItinerary to trigger re-render
      const itCopy: any = JSON.parse(JSON.stringify(editor.itinerary || { days: [] }));
      while ((itCopy.days || []).length <= dayIndex) itCopy.days.push({ day: itCopy.days.length + 1, items: [] });
      const dayItems: any[] = Array.isArray(itCopy.days[dayIndex].items) ? [...itCopy.days[dayIndex].items] : [];
      const idx = replaceIdx;
      if (idx >= 0 && idx < dayItems.length) {
        itCopy.days[dayIndex].items = [...dayItems.slice(0, idx), item, ...dayItems.slice(idx + 1)];
      } else {
        itCopy.days[dayIndex].items = [...dayItems, item];
      }
      console.debug('[TimelineItem] addExternalUrlItem -> item to insert:', item);
      console.debug('[TimelineItem] addExternalUrlItem -> insertion context:', {
        dayIndex,
        replaceIdx: idx,
        originalArrayLength: dayItems.length,
        newArrayLength: itCopy.days[dayIndex].items.length,
        insertedItem: itCopy.days[dayIndex].items[idx],
      });
      editor.setItinerary(itCopy);
      console.debug('[TimelineItem] addExternalUrlItem -> setItinerary completed');
      setDirty && setDirty(true);
      try { showSuccess && showSuccess('已加入嵌入'); } catch (e) {}
      // clear inputs after successful add to avoid stale state
      try { if (isVideoEmbedOnly) setExternalEmbedCode(''); else setExternalUrl(''); } catch (e) {}
      // optionally persist immediately (non-blocking)
      try {
        const resolvedGroupId = (editor && editor.itinerary && editor.itinerary.groupId) ? editor.itinerary.groupId : (groupIdForLocal || null);
        console.log('🔍 检查持久化条件:', {
          hasSupabaseClient: !!supabaseClient,
          resolvedGroupId,
          groupIdForLocal,
          editorGroupId: editor?.itinerary?.groupId,
        });
        
        if (supabaseClient && resolvedGroupId) {
          const groupId = resolvedGroupId;
          const dayDate = selectedDay?.date_str || selectedDay?.dateStr || selectedDay?.date || null;
          console.log('🔍 检查日期条件:', {
            groupId,
            dayDate,
            selectedDay,
          });
          
          if (groupId && dayDate) {
            console.log('✅ 条件通过，开始插入数据库');
            const { data: latest } = await supabaseClient
              .from('group_itinerary_entries')
              .select('sort_order')
              .eq('group_id', groupId)
              .eq('day_date', dayDate)
              .order('sort_order', { ascending: false })
              .limit(1);
            const nextSort = latest && latest.length ? (latest[0].sort_order || 0) + 1 : 1;
            const row = {
              group_id: groupId,
              day_number: dayIndex + 1,
              day_date: dayDate,
              sort_order: nextSort,
              item_type: 'custom',
              creator_id: currentUserId || null,
              card_id: null,
              note: null,
              start_time: item.time || null,
              custom_payload: externalPayload,
              inline_payload: { title, ...externalPayload },
            };
            
            console.log('=== 视频卡片插入调试 ===');
            console.log('1. 准备插入的 row:', row);
            console.log('2. currentUserId:', currentUserId);
            console.log('3. row.creator_id:', row.creator_id);
            
            let insertRes: any = await supabaseClient.from('group_itinerary_entries').insert([row]).select('*').maybeSingle();
            let inserted = insertRes && (insertRes.data ?? insertRes);
            let insertErr = insertRes && (insertRes.error ?? insertRes.error);
            
            console.log('4. 插入结果 insertRes:', insertRes);
            console.log('5. inserted:', inserted);
            console.log('6. inserted?.creator_id:', inserted?.creator_id);
            console.log('7. insertErr:', insertErr);
            
            if (insertErr) {
              console.error('插入失败，错误信息:', insertErr);
              const msg = String(insertErr?.message || insertErr?.msg || insertErr);
              if (msg && /creator_id/.test(msg) && /schema|column|Could not find/i.test(msg)) {
                console.log('检测到 creator_id 字段错误，尝试去掉重试...');
                try {
                  const rowNoCreator = { ...row };
                  delete rowNoCreator.creator_id;
                  const retryRes: any = await supabaseClient.from('group_itinerary_entries').insert([rowNoCreator]).select('*').maybeSingle();
                  inserted = retryRes && (retryRes.data ?? retryRes);
                  insertErr = retryRes && (retryRes.error ?? retryRes.error);
                  console.log('重试结果:', { inserted, insertErr });
                  if (insertErr) throw insertErr;
                } catch (e) {
                  throw insertErr;
                }
              } else {
                throw insertErr;
              }
            }
            
            const serverId = inserted && inserted.id;
            if (serverId) {
              item.id = serverId;
              item.meta = { ...(item.meta || {}), itineraryEntryId: serverId };
              // Post-insert repair: some DB paths strip custom_payload/inline fields
              try {
                const needsPatch = (() => {
                  const cp = inserted?.custom_payload;
                  const ip = inserted?.inline_payload;
                  const isEmptyObj = (v: any) => !v || (typeof v === 'object' && Object.keys(v).length === 0);
                  if (isEmptyObj(cp) || cp?.type !== 'external') return true;
                  if (!ip || ip?.type !== 'external') return true;
                  if (!ip.website && externalPayload.website) return true;
                  return false;
                })();
                    if (needsPatch) {
                  const { data: patched, error: patchErr } = await supabaseClient
                    .from('group_itinerary_entries')
                    .update({
                      item_type: 'custom',
                      custom_payload: externalPayload,
                      inline_payload: { title, type: 'external', ...externalPayload },
                      creator_id: currentUserId || null,
                    })
                    .eq('id', serverId)
                    .select()
                    .maybeSingle();
                  if (!patchErr && patched) {
                    // sync local
                    item.custom_payload = externalPayload;
                    item.payload = externalPayload;
                        // also update local itinerary copy with server id (optional UI sync)
                        try {
                          const itCopy2: any = JSON.parse(JSON.stringify(editor.itinerary || { days: [] }));
                          const di = dayIndex;
                          const arr2: any[] = Array.isArray(itCopy2.days?.[di]?.items) ? [...itCopy2.days[di].items] : [];
                          if (idx >= 0 && idx < arr2.length) {
                            arr2[idx] = { ...(arr2[idx] || {}), id: serverId, meta: { ...((arr2[idx] || {}).meta || {}), itineraryEntryId: serverId } };
                            itCopy2.days[di].items = arr2;
                            editor.setItinerary(itCopy2);
                          }
                        } catch {}
                  }
                }
              } catch (e) {
                // ignore
              }
            }
          }
        }
      } catch (e) {
        // ignore server errors; local state already updated
        console.warn('[TimelineItem] external URL immediate persist failed', e);
      }
    } catch (e) {
      console.error('[TimelineItem] addExternalUrlItem error', e);
    }
  };

  // long-press actions UI
  const [showActions, setShowActions] = useState(false);
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const longPressTimeout = useRef<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!showActions) return;
      // if click is outside our wrapper, hide actions
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowActions(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [showActions]);

  const startLongPress = (e?: any) => {
    // only left mouse button
    if (e && e.type === 'mousedown' && e.button !== 0) return;
    if (longPressTimeout.current) return;
    longPressTimeout.current = window.setTimeout(() => {
      setShowActions(true);
      longPressTimeout.current = null;
    }, 600);
  };

  const cancelLongPress = () => {
    if (longPressTimeout.current) {
      clearTimeout(longPressTimeout.current);
      longPressTimeout.current = null;
    }
    // do NOT hide actions on mouseup/touchend - keep actions visible until user taps outside
  };

  const triggerEdit = (e?: any) => {
    e && e.stopPropagation();
    // prefer inline edit inside the timeline if the parent provided the handler
    const idx = typeof sourceIndex === 'number' && sourceIndex >= 0 ? sourceIndex : index;
    setShowActions(false);
    // Prefer inline edit for transport cards (content) — parent controls whether inline editing is allowed.
    if (isTransport && setInlineEditTarget) {
      console.debug('[TimelineItem] triggerEdit -> setInlineEditTarget (inline content)', { dayIndex, index: idx, cardId });
      setInlineEditTarget({ dayIndex, itemIndex: idx, mode: 'content' });
      return;
    }

    // Fallback: open the fullscreen editor/modal.
    if (setEditItemIndex) setEditItemIndex(idx);
    if (setEditForm) setEditForm(it);
    if (setEditModalVisible) {
      // Guard: only open modal if the inlineEditTarget isn't actively pointing elsewhere
      console.debug('[TimelineItem] triggerEdit -> opening fullscreen modal', { dayIndex, index: idx, cardId, inlineEditTarget });
      setEditModalVisible(true);
      return;
    }

    try {
      setOpenPoi && setOpenPoi({ visible: true, payload: it, dayIndex, itemIndex: idx });
    } catch (e) {}
  };

  const [failedAvatarMap, setFailedAvatarMap] = useState<Record<string, boolean>>({});

  const resolveAuthorProfile = (child: any) => {
    const childCreatorId = child?.creator_id ?? child?.creatorId ?? child?.meta?.creatorId ?? child?.meta?.creator_id ?? null;
    const profileFromChild = child?.creator_profile ?? child?.creatorProfile ?? child?.meta?.creatorProfile ?? null;
    const profileFromMap = childCreatorId && memberProfiles ? memberProfiles[String(childCreatorId)] : undefined;
    const mapDisplayName = profileFromMap ? (profileFromMap.display_name || null) : null;
    const fallbackName = child?.creator_name || child?.creatorName || (profileFromChild && (profileFromChild.name || profileFromChild.nickname || profileFromChild.display_name));
    const displayName = (profileFromChild && (profileFromChild.display_name || profileFromChild.nickname || profileFromChild.name))
      || mapDisplayName
      || fallbackName
      || '旅伴';
    const avatarUrl = (profileFromChild && (profileFromChild.avatar_url || profileFromChild.avatarUrl))
      || (profileFromMap && profileFromMap.avatar_url)
      || child?.creator_avatar_url
      || child?.creatorAvatarUrl
      || null;
    
    // Debug log
    console.log('[TimelineItem] resolveAuthorProfile:', {
      childId: child?.id,
      childCreatorId,
      profileFromChild,
      profileFromMap,
      memberProfilesKeys: memberProfiles ? Object.keys(memberProfiles) : [],
      displayName,
      avatarUrl,
    });
    
    return { creatorId: childCreatorId, displayName, avatarUrl };
  };

  const formatChildTimestamp = (iso?: string | null) => {
    if (!iso) return '';
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return '';
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const datePart = `${dt.getFullYear()}/${pad(dt.getMonth() + 1)}/${pad(dt.getDate())}`;
    const timePart = `${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
    if (dt.getFullYear() === now.getFullYear()) {
      return `${pad(dt.getMonth() + 1)}/${pad(dt.getDate())} ${timePart}`;
    }
    return `${datePart} ${timePart}`;
  };

  const resolveAvatarUrl = (avatarUrl?: string | null): string | null => {
    if (!avatarUrl) return null;
    
    // 处理预设头像
    if (isPresetAvatar(avatarUrl)) {
      const presetAsset = getPresetAvatarAsset(avatarUrl);
      console.log('[TimelineItem] resolveAvatarUrl preset:', { avatarUrl, presetAsset, typeofPreset: typeof presetAsset });
      if (presetAsset) {
        // presetAsset 可能是 require() 返回的模块或者 { uri: string }
        if (typeof presetAsset === 'number') {
          // React Native require() 返回的数字 ID，web 环境下无法直接使用
          // 但 getPresetAvatarAsset 在 web 版本应该返回正确的资源
          return null;
        }
        if (typeof presetAsset === 'string') {
          return presetAsset;
        }
        if (typeof presetAsset === 'object') {
          if ('uri' in (presetAsset as any) && typeof (presetAsset as any).uri === 'string') {
            return (presetAsset as any).uri;
          }
          if ('default' in (presetAsset as any) && typeof (presetAsset as any).default === 'string') {
            return (presetAsset as any).default;
          }
        }
      }
      return null;
    }
    
    const mapped = getAvatarSource(avatarUrl);
    if (!mapped) {
      return avatarUrl.startsWith('http') ? avatarUrl : null;
    }
    if (typeof mapped === 'string') {
      return mapped;
    }
    if (typeof mapped === 'object') {
      if ('uri' in (mapped as any) && typeof (mapped as any).uri === 'string') {
        return (mapped as any).uri;
      }
      if ('default' in (mapped as any) && typeof (mapped as any).default === 'string') {
        return (mapped as any).default;
      }
    }
    return avatarUrl.startsWith('http') ? avatarUrl : null;
  };

  const renderAvatar = (name: string, avatarUrl?: string | null, cacheKey?: string | null) => {
    const displayInitial = name ? name.trim().charAt(0).toUpperCase() : '旅';
    const resolvedUrl = resolveAvatarUrl(avatarUrl);
    const failureKey = cacheKey || (avatarUrl ? `url:${avatarUrl}` : `name:${displayInitial}`);
    const hasFailed = failedAvatarMap[failureKey];
    const avatarSize = 36;
    const fallbackStyle: React.CSSProperties = {
      width: `${avatarSize}px`,
      height: `${avatarSize}px`,
      borderRadius: '50%',
      flexShrink: 0,
      backgroundColor: '#E0E7FF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 16,
      fontWeight: 600,
      color: '#4338CA',
      overflow: 'hidden',
    };
    const imageStyle = {
      width: avatarSize,
      height: avatarSize,
      borderRadius: avatarSize / 2,
    } as any;
    if (resolvedUrl && !hasFailed) {
      return (
        <img
          src={resolvedUrl}
          alt={name || '旅伴'}
          style={{ width: avatarSize, height: avatarSize, borderRadius: avatarSize / 2, objectFit: 'cover', display: 'block' }}
          onError={() => {
            setFailedAvatarMap((prev) => ({ ...prev, [failureKey]: true }));
          }}
        />
      );
    }
    return <div style={fallbackStyle}>{displayInitial}</div>;
  };

  // --- Derived UI state/helpers ---
  // stable card id used for selection / keys: prefer persisted ids, then meta/payload ids, then fallback to positional id
  const cardId = it?.id || it?.meta?.itineraryEntryId || it?.meta?.itinerary_entry_id || it?.payload?.id || `day${dayIndex}-item${index}`;
  const isTransport = it?.type === 'transport';
  const [saving, setSaving] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
  const [confirmDeleteNoteId, setConfirmDeleteNoteId] = useState<string | null>(null);
  const [deletedNoteIds, setDeletedNoteIds] = useState<Set<string>>(new Set()); // 跟踪已删除的 Note IDs
  const isExpanded = expandedCards ? expandedCards.has(String(cardId)) : false;
  const onToggleExpand = () => { try { toggleExpand && toggleExpand(String(cardId)); } catch (e) {} };

  // 获取行程创建者 ID
  const itineraryCreatorId = editor?.itinerary?.creator_id || editor?.itinerary?.creatorId || null;
  
  // 判断当前用户是否可以删除某个 Note
  // 预览模式下（isEditing = false）不显示删除按钮
  const canDeleteNote = (noteCreatorId: string | null) => {
    // 预览模式（右上角切换）不显示删除按钮
    if (!isEditing) return false;
    // 需要有团队编辑权限
    if (!canEdit) return false;
    if (!currentUserId) return false;
    // 行程创建者可以删除所有 notes
    if (itineraryCreatorId && currentUserId === itineraryCreatorId) return true;
    // Note 创建者可以删除自己的 note
    if (noteCreatorId && currentUserId === noteCreatorId) return true;
    return false;
  };

  // 删除 Note 的处理函数
  const handleDeleteNote = async (noteEntryId: string) => {
    if (!noteEntryId || !supabaseClient) {
      showError && showError('無法刪除：缺少必要參數');
      return;
    }
    
    setDeletingNoteId(noteEntryId);
    try {
      const delRes = await supabaseClient.from('group_itinerary_entries').delete().eq('id', noteEntryId);
      const delErr = delRes && (delRes.error ?? (delRes as any).error);
      
      if (delErr) {
        console.error('[TimelineItem] Note delete error', delErr);
        showError && showError('刪除失敗，請稍後重試');
      } else {
        showSuccess && showSuccess('已刪除筆記');
        // 立即更新本地状态，隐藏已删除的 Note
        setDeletedNoteIds(prev => new Set(prev).add(noteEntryId));
      }
    } catch (e) {
      console.error('[TimelineItem] Note delete unexpected error', e);
      showError && showError('刪除失敗');
    } finally {
      setDeletingNoteId(null);
      setConfirmDeleteNoteId(null);
    }
  };

  // connector progress: fraction between this item's time and next item's time
  const nowMinutes = typeof getNowMinutes === 'function' ? getNowMinutes() : (new Date()).getHours() * 60 + (new Date()).getMinutes();
  const thisMinutes = typeof parseTimeToMinutes === 'function' ? parseTimeToMinutes(it?.time) ?? 0 : 0;
  const nextIt = Array.isArray(arr) && arr.length > index + 1 ? arr[index + 1] : null;
  const nextMinutes = nextIt && typeof parseTimeToMinutes === 'function' ? parseTimeToMinutes(nextIt.time) : undefined;
  let progress = 0;
  if (typeof nextMinutes === 'number' && nextMinutes > thisMinutes) {
    progress = (nowMinutes - thisMinutes) / (nextMinutes - thisMinutes);
  } else {
    progress = nowMinutes >= thisMinutes ? 1 : 0;
  }
  // clamp
  if (!isFinite(progress)) progress = 0;
  if (progress < 0) progress = 0;
  if (progress > 1) progress = 1;

  // Choose a more accurate node icon based on transport mode when applicable.
  const deriveTransportNodeIcon = (item: any): string | undefined => {
    if (!item) return undefined;
    const payload = item.payload || item.custom_payload || item.inline_payload || {};
    const modeRaw = (payload && (payload.mode || payload.transport_type || payload.transportMode || payload.transport_type)) || item.mode || item.transport_type || '';
    const mode = String(modeRaw || '').toLowerCase();
    // Map common transport modes to Material icon ligatures (MaterialIcons family is used by Timeline)
    if (!mode) return 'flight'; // default to flight when unknown but transport
    if (mode.includes('flight') || mode === 'air' || mode === 'plane') return 'flight';
    if (mode.includes('ferry') || mode.includes('boat') || mode.includes('ship') || mode.includes('cruise')) return 'directions_boat';
    if (mode.includes('train') || mode.includes('rail')) return 'train';
    if (mode.includes('metro') || mode.includes('subway')) return 'directions_subway';
    if (mode.includes('bus')) return 'directions_bus';
    if (mode.includes('taxi')) return 'local_taxi';
    if (mode.includes('car') || mode.includes('drive')) return 'directions_car';
    if (mode.includes('walk') || mode.includes('foot')) return 'directions_walk';
    if (mode.includes('bike') || mode.includes('bicycle')) return 'directions_bike';
    if (mode.includes('other')) return 'travel_explore';
    // fallback
    return 'travel_explore';
  };

  const nodeIconName = isTransport ? deriveTransportNodeIcon(it) : undefined;
  const nodeColor = ORANGE;
  const nodeSize = 12;

  // Determine whether this item is a placeholder/empty card so we can
  // tweak some presentation (e.g. hide the timeline node/dot for empty slots).
  const hasPlaceholderFlag = it?.meta?.isPlaceholder === true;
  const hasNoTitle = !it?.title || it.title.trim() === '' || it.title === '新景点' || it.title === '新條目' || it.title === '行程条目' || it.title === '条目';
  const hasNoCard = !it?.card || (it.card && Object.keys(it.card).length === 0);
  // External/embed items store data in payload/custom_payload, not card, so check those too
  const hasPayload = (it?.payload && Object.keys(it.payload).length > 0) || (it?.custom_payload && Object.keys(it.custom_payload).length > 0);
  const isEmptyCard = !isTransport && hasNoTitle && hasNoCard && !hasPayload;
  const isPlaceholder = hasPlaceholderFlag || isEmptyCard;

  // --- 強制圖片畫廊反應式更新機制 ---
  // 問題：有些情況圖片已追加到 card.image_gallery，但 React 因為同一層引用未察覺需要重繪，使用者需刷新才看得到。
  // 解法：監聽 image_gallery 長度（以及最後一張的 key / url），觸發一個本地版本號 state 讓子 note 區塊重新渲染。
  const [galleryVersion, setGalleryVersion] = useState(0);
  useEffect(() => {
    try {
      const gallery = it?.card?.image_gallery;
      if (!Array.isArray(gallery)) return;
      // 以長度與最後一張圖片的 key/url 作為變更訊號
      const last = gallery[gallery.length - 1];
      const signature = `${gallery.length}:${last && (last.key || last.url || '')}`;
      // 使用 requestAnimationFrame 避免同步多次 setState 導致額外 reflow
      requestAnimationFrame(() => {
        setGalleryVersion(v => (v >= 1000000 ? 0 : v + 1));
      });
      // 可選：在開發模式打印，避免正式環境噪音
      if (typeof window !== 'undefined' && window.location && /localhost|127\.0\.0\.1/.test(window.location.host)) {
        console.log('[TimelineItem] gallery reactive tick', signature);
      }
    } catch (e) {
      // 忽略錯誤
    }
  // 僅在長度或最後元素 key/url 改變時觸發
  }, [it?.card?.image_gallery?.length, it?.card?.image_gallery && it.card.image_gallery[it.card.image_gallery.length - 1]?.key, it?.card?.image_gallery && it.card.image_gallery[it.card.image_gallery.length - 1]?.url]);

    const innerContent = (
      <div data-item-content="1" style={{ position: 'relative' }}>
          {/* Inline content editing disabled: always use fullscreen modal for content edits. */}
          {inlineEditTarget && inlineEditTarget.dayIndex === dayIndex && (typeof sourceIndex === 'number' && sourceIndex >= 0 ? sourceIndex : index) === inlineEditTarget.itemIndex && inlineEditTarget.mode === 'content' ? (
            // For transport items, render the compact inline TransportForm. For others, open fullscreen modal.
            isTransport ? (
              <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                <TransportForm
                  value={it.payload || it}
                  compact
                  saving={saving}
                  onCancel={() => {
                    setInlineEditTarget && setInlineEditTarget(null);
                  }}
                  onSave={async (payload: any) => {
                    console.debug('[TimelineItem] inline onSave start', { dayIndex, index, cardId });
                    const idx2 = typeof sourceIndex === 'number' && sourceIndex >= 0 ? sourceIndex : index;
                    let serverError = false;
                    try {
                      setSaving(true);

                      // Optimistic update locally
                      if (editor && typeof editor.updateItem === 'function') {
                        editor.updateItem(dayIndex, idx2, { payload, title: payload.title || it.title });
                        try { setDirty && setDirty(true); } catch (e) {}
                      }

                      // Try server sync if we can extract an entry id
                      const extractEntryIdLocal = (obj: any): string | undefined => {
                        if (!obj || typeof obj !== 'object') return undefined;
                        const candidates = [
                          obj.meta && obj.meta.itineraryEntryId,
                          obj.meta && obj.meta.itinerary_entry_id,
                          obj.id,
                          obj.itineraryEntryId,
                          obj.payload && obj.payload.id,
                          obj.payload && obj.payload.itineraryEntryId,
                        ];
                          for (const c of candidates) {
                            if (c !== null && typeof c !== 'undefined' && String(c).trim()) return String(c);
                          }
                          return undefined;
                      };

                      const entryId = extractEntryIdLocal(it) || extractEntryIdLocal(it.payload) || extractEntryIdLocal(it.meta);
                      if (supabaseClient && entryId && canEdit) {
                        try {
                          const updateRow: any = { custom_payload: payload, inline_payload: payload };
                          if (payload.start_time) updateRow.start_time = payload.start_time;
                          if (payload.end_time) updateRow.end_time = payload.end_time;
                          console.debug('[TimelineItem] attempting supabase update', { entryId, updateRow });
                          const res = await supabaseClient.from('group_itinerary_entries').update(updateRow).eq('id', entryId).select().maybeSingle();
                          console.debug('[TimelineItem] supabase update response', res);
                          const err = res && (res.error ?? (res as any).error);
                          if (err) {
                            console.error('[TimelineItem] server update error', err, { entryId, updateRow, res });
                            serverError = true;
                            try { showError && showError('伺服器保存失敗，已在本地保存'); } catch (e) {}
                          } else {
                            try { showSuccess && showSuccess('已保存至伺服器'); } catch (e) {}
                            // If server returned the updated row, ensure local editor item reflects server-side persisted payload
                            try {
                              const returned = res && (res.data ?? res);
                              const updatedRow = Array.isArray(returned) ? returned[0] : returned;
                              if (updatedRow) {
                                const appliedPatch: any = {};
                                if (updatedRow.custom_payload) appliedPatch.custom_payload = updatedRow.custom_payload;
                                if (updatedRow.inline_payload) appliedPatch.inline_payload = updatedRow.inline_payload;
                                // Keep the displayed merged payload reasonably in sync: prefer inline_payload, then custom_payload
                                if (updatedRow.inline_payload) appliedPatch.payload = updatedRow.inline_payload;
                                else if (updatedRow.custom_payload) appliedPatch.payload = updatedRow.custom_payload;
                                if (updatedRow.updated_at) appliedPatch.updated_at = updatedRow.updated_at;
                                // Only apply if we actually have something to patch
                                if (Object.keys(appliedPatch).length > 0) {
                                  console.debug('[TimelineItem] applying server-returned patch to local item', { dayIndex, idx2, appliedPatch });
                                  editor.updateItem(dayIndex, idx2, appliedPatch);
                                  try { setDirty && setDirty(true); } catch (e) {}
                                }
                              }
                            } catch (e) {
                              console.warn('[TimelineItem] failed to apply server-returned row to local editor state', e);
                            }
                          }
                        } catch (e) {
                          console.error('[TimelineItem] server update unexpected', e);
                          serverError = true;
                          try { showError && showError('伺服器保存失敗，已在本地保存: ' + (e && e.message ? e.message : String(e))); } catch (er) {}
                        }
                      }
                      else {
                        console.debug('[TimelineItem] supabase update skipped (no supabaseClient or no entryId or cannot edit)', { hasSupabase: !!supabaseClient, entryId, canEdit });
                      }
                    } catch (e) {
                      console.error('[TimelineItem] inline save failed', e);
                      serverError = true;
                      try { showError && showError('保存失敗: ' + (e && e.message ? e.message : String(e))); } catch (er) {}
                    } finally {
                      setSaving(false);
                      // If server sync failed, keep inline editor open so user can retry; otherwise close
                      if (!serverError) {
                        console.debug('[TimelineItem] inline onSave success -> clearing inlineEditTarget', { dayIndex, index, cardId });
                        setInlineEditTarget && setInlineEditTarget(null);
                      } else {
                        console.debug('[TimelineItem] inline onSave encountered server error; keeping inline editor open', { dayIndex, index, cardId });
                      }
                    }
                  }}
                />
              </div>
            ) : (
              // Non-transport: open modal
              (() => {
                try {
                  const idx2 = typeof sourceIndex === 'number' && sourceIndex >= 0 ? sourceIndex : index;
                  if (setEditItemIndex) setEditItemIndex(idx2);
                  if (setEditForm) setEditForm(it);
                  if (setEditModalVisible) setEditModalVisible(true);
                } catch (e) {}
                return null;
              })()
            )
          ) : (
            <>
              {/* 检查是否为占位卡片或空卡片 */}
              {(() => {
                // 判断卡片是否为空：
                // 1. meta.isPlaceholder 标记
                // 2. 或者没有实质内容（title为空或是默认值，且没有card数据）
                // 注意：交通卡片（transport）是内联数据，不需要 card 对象，不应判断为空
                
                // Robustly resolve payload and type to handle 'custom' items (like external links) correctly
                const payload = it?.payload || it?.custom_payload || it?.inline_payload || {};
                const resolvedType = (it?.type && it.type !== 'custom') ? it.type : (payload?.type || it?.type || 'poi');
                
                const hasPlaceholderFlag = it?.meta?.isPlaceholder === true;
                
                // Check title in both top-level and payload
                const titleCandidate = it?.title || payload?.title;
                const hasNoTitle = !titleCandidate || String(titleCandidate).trim() === '' || titleCandidate === '新景点' || titleCandidate === '新條目' || titleCandidate === '行程条目' || titleCandidate === '条目';
                
                const hasNoCard = !it?.card || (it.card && Object.keys(it.card).length === 0);
                
                const isTransport = resolvedType === 'transport';
                const isExternal = resolvedType === 'external';
                
                // For external items, check if payload has meaningful content (website/embed/etc.)
                const hasExternalContent = isExternal && (
                  payload.website || payload.embed || payload.imageUrl || payload.url
                );
                
                // 只有非交通/非external卡片才用 hasNoTitle+hasNoCard 检查是否为空
                // external 卡片只要有 payload 内容就不算空
                const isEmpty = !isTransport && !isExternal && hasNoTitle && hasNoCard;
                
                const isPlaceholder = hasPlaceholderFlag || (isEmpty && !hasExternalContent);
                
                // Debug: log placeholder detection for external items
                if (it?.type === 'external') {
                  console.debug('[TimelineItem] external item placeholder check', {
                    id: it?.id,
                    title: it?.title,
                    hasPlaceholderFlag,
                    hasNoTitle,
                    hasNoCard,
                    hasExternalContent,
                    isEmpty,
                    isPlaceholder,
                    metaIsPlaceholder: it?.meta?.isPlaceholder,
                  });
                }
                // Do NOT auto-select placeholders. Selection is controlled by parent via
                // `isSelected` prop. For placeholder cards we want the edit UI to show
                // in editing mode but without a drag handle unless the parent provides
                // `renderDragHandle` (i.e. the card is actually selected).
                const isSelectedInternal = Boolean(isSelected);
                
                return isPlaceholder ? (
                  /* 占位卡片：根据是否在编辑模式显示不同UI */
                  (() => {
                    // 根据卡片类型显示对应的图标和文字
                    const typeLabels: Record<string, { icon: string; label: string }> = {
                      poi: { icon: '📍', label: '景点' },
                      meal: { icon: '🍴', label: '餐饮' },
                      restaurant: { icon: '🍴', label: '餐饮' },
                      activity: { icon: '🎉', label: '活动' },
                      lodging: { icon: '🏨', label: '住宿' },
                      transport: { icon: '🚇', label: '交通' },
                      shopping: { icon: '🛒', label: '购物' },
                    };
                    const cardType = it?.type || 'poi';
                    const typeInfo = typeLabels[cardType] || { icon: '📍', label: '卡片' };
                                    const isVideoEmbedOnly = Boolean(it?.meta?.videoEmbedOnly || it?.meta?.video_embed_only);
                    
                    // 在编辑模式且有权限时显示占位卡片的编辑 UI（但不强制显示拖拽手柄）
                      if (canEdit && isEditing) {
                      // external-only 占位：仅显示“粘贴网址并生成预览”的精简 UI
                      if (cardType === 'external' || it?.meta?.externalOnly || isVideoEmbedOnly) {
                        return (
                          <div
                            onPointerDownCapture={(e: any) => {
                              try {
                                const target = e.target as HTMLElement;
                                const closestInteractive = target && (target.closest && target.closest('input, textarea, select, button, a, [contenteditable="true"], [data-select-ignore]'));
                                if (closestInteractive) return;
                              } catch (err) {}
                            }}
                            style={{
                              background: '#FFF',
                              border: '2px dashed #FB7A4A',
                              borderRadius: 12,
                              padding: '20px 16px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 12,
                              alignItems: 'stretch',
                              position: 'relative'
                            }}
                          >
                            <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 4, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                              <span style={{ fontSize: 20 }}>{isVideoEmbedOnly ? '🎬' : '🔗'}</span>
                              <span>{isVideoEmbedOnly ? '貼上 iframe/embed 代碼' : '粘贴网址并生成预览'}</span>
                            </div>

                            {/* 外部网址粘贴 + 解析（精简版） */}
                            <div style={{
                              background: '#F9FAFB',
                              border: '1px solid #E5E7EB',
                              borderRadius: 8,
                              padding: 14,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 10
                            }}>
                              {isVideoEmbedOnly ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                  <textarea
                                    data-select-ignore
                                    placeholder="貼上 iframe 或影片 embed 代碼"
                                    value={externalEmbedCode}
                                    onChange={(e) => setExternalEmbedCode(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => { e.stopPropagation(); }}
                                    style={{
                                      position: 'relative',
                                      zIndex: 1,
                                      width: '100%',
                                      minHeight: 64,
                                      padding: '8px 10px',
                                      borderRadius: 6,
                                      border: '1px solid #D1D5DB',
                                      fontSize: 13,
                                      outline: 'none',
                                      resize: 'vertical',
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                  <div style={{ display: 'flex', gap: 8 }}>
                                    <button
                                      type="button"
                                      data-select-ignore
                                      onMouseDown={(e) => { e.stopPropagation(); try { if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') e.nativeEvent.stopImmediatePropagation(); } catch {} }}
                                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); try { if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') e.nativeEvent.stopImmediatePropagation(); } catch {} if (externalEmbedCode && externalEmbedCode.trim() !== '') addExternalUrlItem(); }}
                                      title="加入嵌入"
                                      style={{
                                        position: 'relative',
                                        zIndex: 3,
                                        flex: 1,
                                        minWidth: 0,
                                        background: '#FB7A4A',
                                        border: '1px solid #FB7A4A',
                                        color: '#FFF',
                                        fontSize: 14,
                                        fontWeight: 600,
                                        padding: '10px 12px',
                                        borderRadius: 6,
                                        cursor: 'pointer',
                                      }}
                                    >加入嵌入</button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <input
                                    data-select-ignore
                                    type="text"
                                    placeholder="https:// ..."
                                    value={externalUrl}
                                    onChange={(e) => setExternalUrl(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && externalUrl && !externalMetaLoading) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        addExternalUrlItem();
                                      }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                      width: '100%',
                                      boxSizing: 'border-box',
                                      padding: '10px 12px',
                                      borderRadius: 6,
                                      border: '1px solid #D1D5DB',
                                      fontSize: 14,
                                      outline: 'none'
                                    }}
                                  />
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    <button
                                      type="button"
                                      data-select-ignore
                                        disabled={!externalUrl || externalMetaLoading}
                                        onMouseDown={(e) => { e.stopPropagation(); }}
                                        onClick={(e) => { e.stopPropagation(); console.debug('[TimelineItem] parse button clicked (top)'); extractMeta(externalUrl); }}
                                      style={{
                                        flex: 1,
                                        minWidth: 0,
                                        background: '#FFF',
                                        border: '1px solid #FB7A4A',
                                        color: '#FB7A4A',
                                        fontSize: 14,
                                        fontWeight: 600,
                                        padding: '10px 12px',
                                        borderRadius: 6,
                                        cursor: externalMetaLoading ? 'default' : 'pointer',
                                        opacity: externalMetaLoading ? 0.6 : 1,
                                        boxSizing: 'border-box'
                                      }}
                                    >{externalMetaLoading ? '解析中…' : '解析網址'}</button>
                                    <button
                                      type="button"
                                      data-select-ignore
                                        disabled={!externalUrl || externalMetaLoading}
                                        onMouseDown={(e) => { e.stopPropagation(); if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') try { e.nativeEvent.stopImmediatePropagation(); } catch {} }}
                                        onClick={(e) => { e.stopPropagation(); if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') try { e.nativeEvent.stopImmediatePropagation(); } catch {} console.debug('[TimelineItem] add-url button clicked (top)'); addExternalUrlItem(); }}
                                      style={{
                                        flex: 1,
                                        minWidth: 0,
                                        background: '#FB7A4A',
                                        border: '1px solid #FB7A4A',
                                        color: '#FFF',
                                        fontSize: 14,
                                        fontWeight: 600,
                                        padding: '10px 12px',
                                        borderRadius: 6,
                                        cursor: externalMetaLoading ? 'default' : 'pointer',
                                        opacity: (!externalUrl || externalMetaLoading) ? 0.6 : 1,
                                        boxSizing: 'border-box'
                                      }}
                                    >加入此網址</button>
                                  </div>
                                </>
                              )}
                              {externalMeta ? (
                                <div style={{
                                  marginTop: 8,
                                  background: '#FFFFFF',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: 6,
                                  padding: '10px 12px',
                                  display: 'flex',
                                  gap: 12
                                }}>
                                  {externalMeta.image ? (
                                    <img src={externalMeta.image} alt="preview" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
                                  ) : null}
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', lineHeight: 1.4 }}>{externalMeta.title || '未解析到标题'}</div>
                                    {externalMeta.description ? (
                                      <div style={{ fontSize: 12, color: '#4B5563', lineHeight: 1.5, maxHeight: 60, overflow: 'hidden' }}>{externalMeta.description}</div>
                                    ) : (
                                      <div style={{ fontSize: 12, color: '#9CA3AF' }}>无描述</div>
                                    )}
                                    <div style={{ fontSize: 11, color: '#6B7280' }}>{externalMeta.siteName || '未知来源'}</div>
                                  </div>
                                </div>
                              ) : null}
                              {externalMeta ? (
                                <div style={{ fontSize: 12, color: '#6B7280' }}>已解析成功：点击「加入此网址」保存，或直接按 Enter 键快速保存。</div>
                              ) : null}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div
                          onPointerDownCapture={(e: any) => {
                            try {
                              const target = e.target as HTMLElement;
                              const closestInteractive = target && (target.closest && target.closest('input, textarea, select, button, a, [contenteditable="true"], [data-select-ignore]'));
                              // Debug only: do not call parent's onSelect here. Parent capture will set selection.
                              console.debug('[TimelineItem] placeholder onPointerDownCapture (no-op)', { tag: target?.tagName, closestInteractive: !!closestInteractive });
                              if (closestInteractive) return;
                              // intentionally do NOT call onSelect to avoid double-toggle (parent already selects on pointerdown capture)
                            } catch (err) {}
                          }}
                          onClick={(e: any) => {
                            try {
                              const target = e.target as HTMLElement;
                              const closestInteractive = target && (target.closest && target.closest('input, textarea, select, button, a, [contenteditable="true"], [data-select-ignore]'));
                              console.debug('[TimelineItem] placeholder onClick (no-op)', { tag: target?.tagName, closestInteractive: !!closestInteractive });
                              if (closestInteractive) return;
                              // Allow click to bubble to parent so parent can decide toggle behavior.
                              // (Previously we stopped propagation unconditionally which prevented
                              // the parent from receiving click events to toggle selection.)
                            } catch (err) {}
                          }}
                          style={{
                            background: '#FFF',
                            border: '2px dashed #FB7A4A',
                            borderRadius: 12,
                            padding: '24px 16px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 12,
                            alignItems: 'stretch',
                            position: 'relative'
                          }}
                        >
                          {/* left-top icons removed: placeholder will use the same action icons as normal cards when selected */}

                          <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 8, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                            <span style={{ fontSize: 20 }}>{typeInfo.icon}</span>
                            <span>选择 <strong style={{ color: '#FB7A4A' }}>{typeInfo.label}</strong> 创建方式</span>
                          </div>

                          {/* 按钮1：去 CardPicker 选择 */}
                          <button
                            type="button"
                            data-select-ignore="true"
                            disabled={isSelectedInternal}
                            onClick={(e: React.MouseEvent) => {
                              // stop propagation in bubble so parent click handlers don't also run
                              e.stopPropagation();
                              if (isSelectedInternal) {
                                console.debug('[占位卡片] 按钮被禁用（已选中）', { cardType, dayIndex, index });
                                return;
                              }
                              const idx = typeof sourceIndex === 'number' && sourceIndex >= 0 ? sourceIndex : index;
                              console.log('[占位卡片] 打开 CardPicker', { cardType, dayIndex, itemIndex: idx });
                              onSelectFromCardPicker && onSelectFromCardPicker(dayIndex, idx);
                            }}
                            style={{
                              backgroundColor: '#F3F4F6',
                              borderRadius: 8,
                              padding: 14,
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 8,
                              cursor: isSelectedInternal ? 'default' : 'pointer',
                              border: '1px solid #E5E7EB',
                              opacity: isSelectedInternal ? 0.5 : 1,
                            }}
                          >
                            <span style={{ fontSize: 18 }}>📋</span>
                            <span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>
                              从卡片库选择
                            </span>
                          </button>

                          {/* 按钮2：打开全屏模态自己创建 */}
                          <button
                            type="button"
                            data-select-ignore="true"
                            disabled={isSelectedInternal}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              if (isSelectedInternal) {
                                console.debug('[占位卡片] 创建按钮被禁用（已选中）', { cardType, dayIndex, index });
                                return;
                              }
                              console.log('[占位卡片] 打开全屏编辑器', { cardType, it, dayIndex, index });
                              
                              // 根据卡片类型创建对应的空 payload
                              let emptyPayload: any;
                              switch (cardType) {
                                case 'poi':
                                  emptyPayload = createEmptyPoiPayload();
                                  break;
                                case 'meal':
                                case 'restaurant':
                                  emptyPayload = createEmptyMealPayload();
                                  break;
                                case 'activity':
                                  emptyPayload = createEmptyActivityPayload();
                                  break;
                                case 'lodging':
                                  emptyPayload = createEmptyLodgingPayload();
                                  break;
                                case 'shopping':
                                  emptyPayload = createEmptyShoppingPayload();
                                  break;
                                case 'transport':
                                  // Transport 使用内联编辑，不需要 payload
                                  emptyPayload = { type: 'transport', mode: 'flight' };
                                  break;
                                default:
                                  emptyPayload = createEmptyPoiPayload();
                              }
                              
                              // 打开全屏 POI 模态，传递空 payload
                              // 标记来自占位卡片，这样父组件在编辑模式下也能识别并允许打开
                              const idx = typeof sourceIndex === 'number' && sourceIndex >= 0 ? sourceIndex : index;
                              setOpenPoi && setOpenPoi({ visible: true, payload: emptyPayload, dayIndex, itemIndex: idx, _fromPlaceholder: true });
                            }}
                            style={{
                              backgroundColor: '#FB7A4A',
                              borderRadius: 8,
                              padding: 14,
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 8,
                              cursor: isSelectedInternal ? 'default' : 'pointer',
                              opacity: isSelectedInternal ? 0.5 : 1,
                              color: '#FFF',
                            }}
                          >
                            <span style={{ fontSize: 18 }}>✏️</span>
                            <span style={{ fontSize: 14, fontWeight: 500, color: '#FFF' }}>
                              自己创建卡片
                            </span>
                          </button>
                        </div>
                      );
                    } else {
                      // 非编辑模式（分享模式）：显示友好的引导提示，点击引导注册/登录
                      return (
                        <TouchableOpacity
                          onPress={() => {
                            // 引导用户去注册页面，保留当前页面作为返回地址
                            console.log('[空卡片引导] 引导用户注册/登录');
                            if (typeof window !== 'undefined') {
                              const currentPath = window.location.pathname + window.location.search;
                              window.location.href = '/auth/register?redirect=' + encodeURIComponent(currentPath);
                            }
                          }}
                          style={{
                            background: 'linear-gradient(135deg, rgba(251, 122, 74, 0.03) 0%, rgba(251, 122, 74, 0.08) 100%)',
                            border: '1.5px dashed #E0E0E0',
                            borderRadius: 16,
                            padding: '32px 20px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 12,
                            cursor: 'pointer',
                            transition: 'all 0.25s ease',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                          }}
                          onMouseEnter={(e: any) => {
                            e.currentTarget.style.borderColor = '#FB7A4A';
                            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(251, 122, 74, 0.05) 0%, rgba(251, 122, 74, 0.12) 100%)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(251, 122, 74, 0.15)';
                          }}
                          onMouseLeave={(e: any) => {
                            e.currentTarget.style.borderColor = '#E0E0E0';
                            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(251, 122, 74, 0.03) 0%, rgba(251, 122, 74, 0.08) 100%)';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                          }}
                        >
                          <span style={{ fontSize: 28, opacity: 0.35, marginBottom: 8 }}>{typeInfo.icon}</span>
                          <div style={{ 
                            fontSize: 15, 
                            color: '#4B5563',
                            textAlign: 'center',
                            lineHeight: 1.6,
                          }}>
                            <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 16 }}>
                              这个行程还未确定
                            </div>
                            <div style={{ fontSize: 14, color: '#9CA3AF', fontWeight: 400, marginBottom: 8 }}>
                              请你加入编辑
                            </div>
                          </div>
                        </TouchableOpacity>
                      );
                    }
                  })()
                ) : (
                  /* 正常卡片：渲染 CardRenderer */
                  (() => {
                    const idx = typeof sourceIndex === 'number' && sourceIndex >= 0 ? sourceIndex : index;
                    return (
                      <div
                        style={{ position: 'relative' }}
                        // onClick removed: interaction handled by parent TimelineList
                      >
                        <CardRenderer 
                          it={it} 
                          isExpanded={isExpanded} 
                          onToggleExpand={onToggleExpand} 
                          onOpenPoi={(payload: any) => setOpenPoi({ visible: true, payload, dayIndex, itemIndex: idx })} 
                          dayDate={selectedDay?.date_str || selectedDay?.dateStr} 
                          onImageClick={onImageClick}
                        />
                      </div>
                    );
                  })()
                );
              })()}
              {/* In-card overlay shown after long-press. Covers card content and shows edit/delete icons. */}
              {canEdit && isEditing && showActions ? (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(255,255,255,0.92)',
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 20,
                    boxSizing: 'border-box',
                    zIndex: 5000 /* intentionally lower than icon z-index so icons sit above overlay */
                  }}
                >
                  <button onClick={(e) => { triggerEdit(e); }} style={{ background: '#FF8A4A', color: '#fff', border: 'none', borderRadius: 28, width: 56, height: 56, cursor: 'pointer', boxShadow: '0 8px 24px rgba(0,0,0,0.14)' }} title="編輯">
                    ✎
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); attemptDelete(); setShowActions(false); }} style={{ background: '#EF4444', color: '#fff', border: 'none', borderRadius: 28, width: 56, height: 56, cursor: 'pointer', boxShadow: '0 8px 24px rgba(0,0,0,0.14)' }} title="刪除">
                    🗑
                  </button>
                </div>
              ) : null}

              {/* placeholder-specific delete modal removed; placeholders now use the same action icons/flows as normal cards */}

              {/* Render action icons (drag / edit / delete) after overlay so they sit above it and remain interactive */}
              {renderDragHandle && (
                <div style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                  <div style={{ pointerEvents: 'auto' }}>{renderDragHandle()}</div>
                </div>
              )}

              {Array.isArray((it as any)?.children) && (it as any).children.filter((child: any) => {
                const childId = child?.id || child?.meta?.itineraryEntryId || child?.meta?.itinerary_entry_id;
                return !childId || !deletedNoteIds.has(childId);
              }).length > 0 ? (
                <div
                  style={{
                    marginTop: 12,
                    paddingLeft: 24,
                    borderLeft: '2px solid rgba(226, 232, 240, 0.9)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                  }}
                >
                  {(() => {
                    const childrenRaw = (it as any).children as any[];
                    // 过滤掉已删除的 Notes
                    const childrenFiltered = childrenRaw.filter((child: any) => {
                      const childId = child?.id || child?.meta?.itineraryEntryId || child?.meta?.itinerary_entry_id;
                      return !childId || !deletedNoteIds.has(childId);
                    });
                    const childrenSorted = [...childrenFiltered].sort((a: any, b: any) => {
                      // 需求：按時間倒序（新的在上面）。
                      const aTime = new Date(a?.updated_at || a?.updatedAt || a?.created_at || a?.createdAt || 0).getTime();
                      const bTime = new Date(b?.updated_at || b?.updatedAt || b?.created_at || b?.createdAt || 0).getTime();
                      if (bTime !== aTime) return bTime - aTime; // DESC
                      // 若時間相同，最後用 childSort 再到 id 做穩定性處理
                      const aSort = typeof a?.meta?.childSort === 'number' ? a.meta.childSort : Number.POSITIVE_INFINITY;
                      const bSort = typeof b?.meta?.childSort === 'number' ? b.meta.childSort : Number.POSITIVE_INFINITY;
                      if (aSort !== bSort) return aSort - bSort;
                      const aId = String(a?.id || '');
                      const bId = String(b?.id || '');
                      return aId.localeCompare(bId);
                    });
                    return childrenSorted.map((child: any, childIndex: number) => {
                    const childTitle = child?.title || child?.payload?.title || '附註';
                    const childText = child?.note || child?.payload?.note || childTitle;
                    const childLinkRaw =
                      (child?.payload && child.payload.mapLink) ||
                      (child?.custom_payload && child.custom_payload.mapLink) ||
                      (child?.meta && child.meta.mapLink);
                    const childMapLink = normalizeExternalLink(childLinkRaw);
                    const childMapModeRaw =
                      child?.payload?.mapLinkMode ||
                      child?.custom_payload?.mapLinkMode ||
                      child?.meta?.mapLinkMode ||
                      child?.payload?.routeMode ||
                      child?.payload?.transportMode ||
                      child?.payload?.mode;
                    const childMapModeMeta = resolveMapRouteModeMeta(childMapModeRaw);
                    const childMapDuration = coalesceText(
                      child?.payload?.mapLinkDuration,
                      child?.custom_payload?.mapLinkDuration,
                      child?.meta?.mapLinkDuration,
                      child?.payload?.routeDuration,
                      child?.payload?.durationText,
                      child?.payload?.duration,
                      child?.payload?.eta
                    );
                    const { displayName, avatarUrl, creatorId: childCreatorId } = resolveAuthorProfile(child);
                    const timestamp = formatChildTimestamp(
                      child?.updated_at
                        || child?.updatedAt
                        || child?.meta?.updatedAt
                        || child?.meta?.updated_at
                        || child?.created_at
                        || child?.createdAt
                    );
                    const childEntryId = child?.id || child?.meta?.itineraryEntryId || child?.meta?.itinerary_entry_id || null;
                    return (
                      <div
                        key={child?.id || `${cardId}-child-${childIndex}`}
                        data-note-entry-id={childEntryId || undefined}
                        style={{
                          background: '#F9FAFB',
                          borderRadius: 12,
                          padding: '12px 16px',
                          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 12,
                        }}
                      >
                        {renderAvatar(displayName, avatarUrl, childCreatorId || child?.id)}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, color: '#1F2937', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                            {childText}
                          </div>
                          {(() => {
                            // 只读 note-level images (custom_payload/payload) 和 local_images
                            const noteLevelImages = (child?.custom_payload?.images || child?.payload?.images) as any[] | undefined;
                            const localImages = (child as any)?.local_images as any[] | undefined;
                            
                            let galleryImages: any[] = [];
                            if (Array.isArray(noteLevelImages) && noteLevelImages.length > 0) {
                              galleryImages = noteLevelImages;
                            } else if (Array.isArray(localImages) && localImages.length > 0) {
                              galleryImages = localImages;
                            }
                            
                            if (galleryImages.length > 9) galleryImages = galleryImages.slice(0, 9);
                            
                            if (typeof window !== 'undefined' && /localhost|127\.0\.0\.1/.test(window.location.host)) {
                              console.log('[TimelineItem child images]', {
                                noteId: child?.id,
                                noteLevel: noteLevelImages?.length || 0,
                                local: localImages?.length || 0,
                                rendered: galleryImages.length
                              });
                            }
                            
                            if (galleryImages.length === 0) return null;
                            
                            return (
                              <div style={{
                                marginTop: 8,
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: 4,
                                maxWidth: 280,
                              }}>
                                {galleryImages.slice(0, 9).map((img: any, idx: number) => (
                                  <div
                                    key={img.key || idx}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      console.log('[TimelineItem] Child note image clicked', { hasOnImageClick: !!onImageClick });
                                      if (onImageClick) {
                                        onImageClick(galleryImages, idx);
                                      } else if (typeof window !== 'undefined') {
                                        window.open(img.url, '_blank');
                                      }
                                    }}
                                    style={{
                                      position: 'relative',
                                      paddingBottom: '100%',
                                      borderRadius: 6,
                                      overflow: 'hidden',
                                      background: '#F3F4F6',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <img
                                      src={img.url}
                                      alt={`图片 ${idx + 1}`}
                                      style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        border: 'none',
                                        display: 'block',
                                      }}
                                    />
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                          {childMapLink ? (
                            <a
                              href={childMapLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                // Extend the clickable map link pill near the avatar,
                                // but keep a small gutter (~8px) from the avatar edge.
                                // Avatar 36px + gap 12px = 48px; use 40px to leave ~8px space.
                                marginTop: 18,
                                marginLeft: -40,
                                width: 'calc(100% + 40px)',
                                padding: '12px 16px',
                                borderRadius: 12,
                                background: '#EEF2FF',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                textDecoration: 'none',
                                boxSizing: 'border-box',
                                border: '1px solid rgba(79, 70, 229, 0.15)',
                                gap: 12,
                                // Subtle elevation + hover affordance
                                boxShadow: '0 2px 6px rgba(79,70,229,0.08)',
                                transition: 'background 120ms ease, box-shadow 120ms ease',
                              }}
                              onMouseEnter={(e) => {
                                (e.currentTarget as HTMLAnchorElement).style.background = '#E0E7FF';
                                (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 4px 12px rgba(79,70,229,0.18)';
                              }}
                              onMouseLeave={(e) => {
                                (e.currentTarget as HTMLAnchorElement).style.background = '#EEF2FF';
                                (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 2px 6px rgba(79,70,229,0.08)';
                              }}
                            >
                              <span style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#1D4ED8', fontSize: 13, fontWeight: 600 }}>
                                  <WebIcon name={childMapModeMeta?.icon || 'near_me'} size={18} color="#2563EB" family="MaterialIcons" />
                                  <span>Google 地圖路線</span>
                                </span>
                                {childMapModeMeta || childMapDuration ? (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#4B5563', fontSize: 12, flexWrap: 'wrap' }}>
                                    {childMapModeMeta ? <span>{childMapModeMeta.shortLabel || childMapModeMeta.label}</span> : null}
                                    {childMapModeMeta && childMapDuration ? <span style={{ color: '#9CA3AF' }}>·</span> : null}
                                    {childMapDuration ? <span>{childMapDuration}</span> : null}
                                  </span>
                                ) : null}
                              </span>
                              <WebIcon name="google-maps" size={20} color="#2563EB" />
                            </a>
                          ) : null}
                          {/* 時間戳和刪除按鈕 */}
                          <div
                            style={{
                              marginTop: 8,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              fontSize: 12,
                              color: '#6B7280',
                            }}
                          >
                            {/* 刪除按鈕 - 只有有權限時顯示 */}
                            {canDeleteNote(childCreatorId) ? (
                              confirmDeleteNoteId === childEntryId ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteNote(childEntryId!);
                                    }}
                                    disabled={deletingNoteId === childEntryId}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 28,
                                      height: 28,
                                      backgroundColor: '#EF4444',
                                      color: '#fff',
                                      border: 'none',
                                      borderRadius: '50%',
                                      cursor: deletingNoteId === childEntryId ? 'wait' : 'pointer',
                                      opacity: deletingNoteId === childEntryId ? 0.6 : 1,
                                    }}
                                    title="確定刪除"
                                  >
                                    <MdCheck size={16} />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setConfirmDeleteNoteId(null);
                                    }}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 28,
                                      height: 28,
                                      backgroundColor: '#E5E7EB',
                                      color: '#374151',
                                      border: 'none',
                                      borderRadius: '50%',
                                      cursor: 'pointer',
                                    }}
                                    title="取消"
                                  >
                                    <MdClose size={16} />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setConfirmDeleteNoteId(childEntryId);
                                  }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 28,
                                    height: 28,
                                    backgroundColor: 'transparent',
                                    color: '#9CA3AF',
                                    border: '1px solid #E5E7EB',
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    transition: 'all 150ms ease',
                                  }}
                                  onMouseEnter={(e) => {
                                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#FEE2E2';
                                    (e.currentTarget as HTMLButtonElement).style.borderColor = '#FECACA';
                                    (e.currentTarget as HTMLButtonElement).style.color = '#EF4444';
                                  }}
                                  onMouseLeave={(e) => {
                                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                                    (e.currentTarget as HTMLButtonElement).style.borderColor = '#E5E7EB';
                                    (e.currentTarget as HTMLButtonElement).style.color = '#9CA3AF';
                                  }}
                                  title="刪除筆記"
                                >
                                  <MdDelete size={14} />
                                </button>
                              )
                            ) : <div />}
                            {/* 時間戳 */}
                            {timestamp ? <span>{timestamp}</span> : <span />}
                          </div>
                        </div>
                      </div>
                    );
                  }); })()}
                </div>
              ) : null}
            </>
          )}
        </div>
  );

  return (
    <div 
      ref={wrapperRef}
      onContextMenu={(e: any) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      // 🎯 暂时禁用长按编辑/删除功能，避免与拖拽冲突
      // onMouseDown={startLongPress}
      // onMouseUp={cancelLongPress}
      // onMouseLeave={cancelLongPress}
      // onTouchStart={startLongPress}
      // onTouchEnd={cancelLongPress}
      style={{
        userSelect: 'none',
        WebkitUserSelect: 'none',
        position: 'relative'
      }}
    >
      {hideTimeColumn ? (
        <View style={{ flex: 1, paddingLeft: 0, paddingRight: 20, alignSelf: 'stretch', paddingBottom: 16 }}>
          {innerContent}
        </View>
      ) : (
        <TimelineItemRow
          key={it.id || index}
          time={it.time || '09:00'}
          showConnectorBelow={index < arr.length - 1}
          connectorProgress={progress}
          // hide the timeline node (the small dot) for placeholder/empty cards so
          // they don't render an overlapping visual marker on the left
          showNode={!isPlaceholder}
          nodeIconName={nodeIconName}
          nodeColor={nodeColor}
          nodeSize={nodeSize}
          onPressTime={() => {
            const idx = typeof sourceIndex === 'number' && sourceIndex >= 0 ? sourceIndex : index;
            setInlineEditTarget && setInlineEditTarget({ dayIndex, itemIndex: idx, mode: 'time' });
          }}
        >
          {innerContent}
        </TimelineItemRow>
      )}
    </div>
  );
};

export default TimelineItem;
